import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Skeleton } from '@et/ui';
import { createBrowserRouter, Link, Navigate, useLocation, useParams } from 'react-router';
import type { RouteObject } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { Providers } from './providers.js';
import { Shell } from './Shell.js';
import { ExplorePage } from '../features/explore/ExplorePage.js';
import { TopicPage } from '../features/topic/TopicPage.js';
import { fetchMeta, queryKeys, type ContentRev } from '../lib/api.js';

/**
 * [05 §9] — o orcamento de bundle e de **180 KB gzip no arranque**, e a unica
 * forma de o cumprir com esta quantidade de app e nao carregar o que nao se
 * esta a ver.
 *
 * No chunk inicial fica a chegada: a timeline e o topico. Tudo o que se abre a
 * partir dali — a ficha de evidencia, o claim com o seu grafo, o resto — e uma
 * **navegacao**, e quarenta milissegundos a carregar um chunk num clique nao
 * se veem. O que se veria era o segundo e meio a mais no primeiro ecra.
 *
 * O `page()` embrulha cada um em `Suspense` com um esqueleto **atrasado**:
 * nos primeiros 150 ms nao aparece nada, porque um esqueleto durante 40 ms e
 * mais ruidoso do que nada; a partir dai aparece, porque um ecra branco
 * durante um segundo diz "partiu" — e viu-se acontecer numa rede lenta.
 */
function DelayedSkeleton() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const h = window.setTimeout(() => setShow(true), 150);
    return () => window.clearTimeout(h);
  }, []);
  if (!show) return null;
  // O mesmo ritmo do marcador de arranque no `index.html` — dois cartoes, os
  // mesmos pesos. Sao dois momentos de espera diferentes (um antes de o React
  // existir, outro entre rotas) e nao ha razao para se parecerem diferentes.
  return (
    <>
      <Card>
        <CardBody>
          <Skeleton width="42%" height="26px" />
          <Skeleton width="78%" height="14px" className="et-skeleton--gap" />
        </CardBody>
      </Card>
      <Card className="et-skeleton--gap">
        <CardBody>
          <Skeleton width="55%" height="20px" />
          <Skeleton width="90%" height="14px" className="et-skeleton--gap" />
          <Skeleton width="64%" height="14px" />
        </CardBody>
      </Card>
    </>
  );
}

function page<P extends object>(load: () => Promise<{ default: React.ComponentType<P> }>) {
  const Component = lazy(load);
  return (props: P) => (
    <Suspense fallback={<DelayedSkeleton />}><Component {...props} /></Suspense>
  );
}

const EvidencePage = page(async () => ({
  default: (await import('../features/evidence/EvidencePage.js')).EvidencePage,
}));
const ClaimPage = page(async () => ({
  default: (await import('../features/claim/ClaimPage.js')).ClaimPage,
}));
const StressTestPage = page(async () => ({
  default: (await import('../features/assessment/StressTestPage.js')).StressTestPage,
}));
const CorpusPage = page(async () => ({
  default: (await import('../features/text/CorpusPage.js')).CorpusPage,
}));
const FigurePage = page(async () => ({
  default: (await import('../features/text/FigurePage.js')).FigurePage,
}));
const ComparePage = page(async () => ({
  default: (await import('../features/compare/ComparePage.js')).ComparePage,
}));
const CompareQuestionPage = page(async () => ({
  default: (await import('../features/compare/CompareQuestionPage.js')).CompareQuestionPage,
}));
const PlayIndexPage = page(async () => ({
  default: (await import('../features/play/PlayIndexPage.js')).PlayIndexPage,
}));
const PlayRunner = page(async () => ({
  default: (await import('../features/play/PlayRunner.js')).PlayRunner,
}));
const CoursesPage = page(async () => ({
  default: (await import('../features/learn/CoursesPage.js')).CoursesPage,
}));
const CoursePage = page(async () => ({
  default: (await import('../features/learn/CoursePage.js')).CoursePage,
}));
const CourseChangesPage = page(async () => ({
  default: (await import('../features/learn/CourseChangesPage.js')).CourseChangesPage,
}));
const LessonPage = page(async () => ({
  default: (await import('../features/learn/LessonPage.js')).LessonPage,
}));
const DashboardPage = page(async () => ({
  default: (await import('../features/account/AccountPage.js')).DashboardPage,
}));
const ProfilePage = page(async () => ({
  default: (await import('../features/account/AccountPage.js')).ProfilePage,
}));
const SavedPage = page(async () => ({
  default: (await import('../features/account/AccountPage.js')).SavedPage,
}));
const MethodologyPage = page(async () => ({
  default: (await import('../features/methodology/MethodologyPage.js')).MethodologyPage,
}));
const SupportPage = page(async () => ({
  default: (await import('../features/support/SupportPage.js')).SupportPage,
}));
const ObjectionsPage = page(async () => ({
  default: (await import('../features/objections/ObjectionsPage.js')).ObjectionsPage,
}));
const ModerationPage = page(async () => ({
  default: (await import('../features/moderation/ModerationPage.js')).ModerationPage,
}));

/**
 * O backoffice e um chunk a parte, so carregado quando alguem la vai.
 * Sem a claim de admin, a API devolve 404 e a pagina nao mostra nada.
 */
const StudioPage = lazy(async () => ({
  default: (await import('../features/studio/StudioPage.js')).StudioPage,
}));

import { DEFAULT_LOCALE, fromUrlSegment, localePath, toUrlSegment } from '../lib/locale.js';

/** Resolve a revisao de conteudo uma vez e passa-a a arvore. */
function WithMeta({ children }: { readonly children: (rev: ContentRev) => React.ReactNode }) {
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: fetchMeta, staleTime: 60_000 });
  if (meta.data === undefined) return null;
  return <>{children({ version: meta.data.contentVersion, checksum: meta.data.checksum })}</>;
}

function devZoomLab() {
  const Page = lazy(async () => ({
    default: (await import('../features/lab/ZoomLabPage.js')).ZoomLabPage,
  }));
  return <Suspense fallback={null}><Page /></Suspense>;
}

/**
 * Um caminho sem lingua a frente nao e um 404.
 *
 * `/evidence/pilate-stone` casa `:locale = "evidence"`, e o que a pessoa
 * queria era a ficha e nao a porta. Prefixa-se com a lingua por omissao **e
 * guarda-se o resto do caminho**, uma unica vez: depois do salto o primeiro
 * segmento ja e uma lingua valida, e este ramo nao volta a disparar.
 */
function LocaleRoot({ client }: { readonly client: QueryClient }) {
  const { locale: segment = '' } = useParams();
  const { pathname, search, hash } = useLocation();
  const locale = fromUrlSegment(segment);
  if (locale === null) {
    return <Navigate to={`/${toUrlSegment(DEFAULT_LOCALE)}${pathname}${search}${hash}`} replace />;
  }
  return (
    <Providers locale={locale} client={client}>
      <Shell locale={locale} />
    </Providers>
  );
}

function LocaleRoute({ render }: { readonly render: (locale: string, rev: ContentRev) => React.ReactNode }) {
  const { locale: segment = '' } = useParams();
  const locale = fromUrlSegment(segment) ?? DEFAULT_LOCALE;
  return <WithMeta>{(rev) => render(locale, rev)}</WithMeta>;
}

/**
 * Dentro de uma lingua valida, um caminho que nao existe e mesmo um 404.
 *
 * Ate aqui o catch-all de topo prefixava tudo o que nao casasse, incluindo o
 * que ja tinha lingua: `/en/explore` virava `/en/en/explore`, e dai
 * `/en/en/en/explore`, sem fim — a app ficava em branco com o React a
 * queixar-se da profundidade de actualizacao. Parar aqui e o que quebra o
 * ciclo, e nao um contador de saltos.
 */
function NotFoundPage() {
  const { t } = useTranslation();
  const { locale: segment = toUrlSegment(DEFAULT_LOCALE) } = useParams();
  const locale = fromUrlSegment(segment) ?? DEFAULT_LOCALE;
  // Um beco sem saida ensina a fechar o separador. Quatro portas, nao uma.
  const doors = [
    { href: localePath(locale), label: t('nav.explore') },
    { href: localePath(locale, 'text'), label: t('text.corpusTitle') },
    { href: localePath(locale, 'compare'), label: t('compare.title') },
    { href: localePath(locale, 'objections'), label: t('nav.objections') },
    { href: localePath(locale, 'methodology'), label: t('nav.methodology') },
  ];
  return (
    <div className="et-detail et-notfound">
      <h1 className="et-detail__title">{t('notFound.title')}</h1>
      <p className="et-prose">{t('notFound.body')}</p>
      <p className="et-notfound__label">{t('notFound.goTo')}</p>
      <ul className="et-notfound__doors">
        {doors.map((d) => <li key={d.href}><Link to={d.href}>{d.label}</Link></li>)}
      </ul>
    </div>
  );
}

export function routeTree(client: QueryClient): RouteObject[] {
  return [
    /*
      Enquanto o selector de língua estiver escondido, a raiz vai para inglês.

      Detectar a língua do browser e mandar um visitante português para `/pt`
      era coerente quando havia como voltar atrás. Sem o selector, seria uma
      escolha sem porta de saída — e a prosa longa ainda está em inglês nessas
      páginas. Os caminhos `/pt` e `/es` continuam a servir quem lá chegue.
    */
    { path: '/', element: <Navigate to={`/${toUrlSegment(DEFAULT_LOCALE)}`} replace /> },
    {
      path: '/:locale',
      element: <LocaleRoot client={client} />,
      children: [
        { index: true, element: <LocaleRoute render={(l, r) => <ExplorePage locale={l as never} rev={r} />} /> },
        { path: 'evidence/:id', element: <LocaleRoute render={(l, r) => <EvidencePage locale={l as never} rev={r} />} /> },
        { path: 'claim/:id', element: <LocaleRoute render={(l, r) => <ClaimPage locale={l as never} rev={r} />} /> },
        { path: 'topic/:slug', element: <LocaleRoute render={(l, r) => <TopicPage locale={l as never} rev={r} />} /> },
        { path: 'text', element: <LocaleRoute render={(l, r) => <CorpusPage locale={l as never} rev={r} />} /> },
        { path: 'play', element: <LocaleRoute render={(l, r) => <PlayIndexPage locale={l as never} rev={r} />} /> },
        { path: 'play/:id', element: <LocaleRoute render={(l, r) => <PlayRunner locale={l as never} rev={r} />} /> },
        { path: 'play/:id/:step', element: <LocaleRoute render={(l, r) => <PlayRunner locale={l as never} rev={r} />} /> },
        { path: 'compare', element: <LocaleRoute render={(l, r) => <ComparePage locale={l as never} rev={r} />} /> },
        { path: 'compare/:id', element: <LocaleRoute render={(l, r) => <CompareQuestionPage locale={l as never} rev={r} />} /> },
        { path: 'learn', element: <LocaleRoute render={(l, r) => <CoursesPage locale={l as never} rev={r} />} /> },
        { path: 'learn/:id', element: <LocaleRoute render={(l, r) => <CoursePage locale={l as never} rev={r} />} /> },
        { path: 'learn/:id/changes', element: <LocaleRoute render={(l, r) => <CourseChangesPage locale={l as never} rev={r} />} /> },
        { path: 'learn/:id/lesson/:lessonId', element: <LocaleRoute render={(l, r) => <LessonPage locale={l as never} rev={r} />} /> },
        { path: 'support', element: <LocaleRoute render={() => <SupportPage />} /> },
        { path: 'methodology', element: <LocaleRoute render={(l, r) => <MethodologyPage locale={l as never} rev={r} />} /> },
        { path: 'me', element: <LocaleRoute render={(l) => <DashboardPage locale={l as never} />} /> },
        { path: 'me/profile', element: <LocaleRoute render={(l) => <ProfilePage locale={l as never} />} /> },
        { path: 'saved', element: <LocaleRoute render={(l) => <SavedPage locale={l as never} />} /> },
        { path: 'figure/:id', element: <LocaleRoute render={(l, r) => <FigurePage locale={l as never} rev={r} />} /> },
        { path: 'objections', element: <LocaleRoute render={(l, r) => <ObjectionsPage locale={l as never} rev={r} />} /> },
        // Sem a claim, a API responde 404 e a pagina nao mostra nada. Nao esta
        // na barra de proposito: quem tem o papel sabe o endereco.
        { path: 'moderation', element: <Suspense fallback={null}><ModerationPage /></Suspense> },
        { path: 'claim/:id/stress', element: <LocaleRoute render={(l, r) => <StressTestPage locale={l as never} rev={r} />} /> },
        { path: 'studio', element: <Suspense fallback={null}><StudioPage /></Suspense> },
        // Banco de ensaio do zoom. O `lazy` vive dentro da condicao para que
        // o bundler nao veja sequer o import em producao — declara-lo fora
        // fazia sair um chunk morto no build.
        ...(import.meta.env.DEV ? [{ path: 'lab/zoom', element: devZoomLab() }] : []),
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}

export const createRouter = (client: QueryClient) => createBrowserRouter(routeTree(client));
